import { useEffect, useState } from "react";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Copy, Mail, Link2, Send } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABELS = { credit_solo: "Credit only — $275 + $25/mo", bundle: "Tax + Credit bundle — $900 · $0/mo" };

function InvitePanel() {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", plan: "credit_solo" });
  const [created, setCreated] = useState(null);

  const load = () => api.get("/platform/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const linkFor = (token) => `${window.location.origin}/agency-signup?invite=${token}`;

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/platform/invites", form);
      const link = linkFor(r.data.token);
      setCreated({ ...form, link, emailed: r.data.emailed });
      setForm({ name: "", email: "", plan: "credit_solo" });
      toast.success(r.data.emailed ? `Invite emailed to ${form.email} — expires in 7 days` : "Invite link created — expires in 7 days");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const resend = async (inv) => {
    try {
      await api.post(`/platform/invites/${inv.id}/send`);
      toast.success(`Invite emailed to ${inv.email}`);
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied — paste it in a text or DM"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const mailto = (inv) =>
    `mailto:${inv.email}?subject=${encodeURIComponent("Your white-label credit repair platform is ready")}&body=${encodeURIComponent(`Hi ${inv.name},\n\nYour white-label workspace invite is ready. Click the link below to create your account — it takes about 60 seconds:\n\n${inv.link || linkFor(inv.token)}\n\nThis link expires in 7 days and locks in your plan: ${PLAN_LABELS[inv.plan]}.\n\nOnce you're in, open Settings to add your logo, company name, and payment options (CashApp handle + your fee structure), then check the Help Center for the step-by-step beginner guide.\n\n— D&Z Management`)}`;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="invite-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Send className="h-4 w-4" /> Invite an agency — click &amp; send</div>
      <p className="text-xs text-slate-400">Generate a personal signup link. They click it, set a password, and get their own workspace with branding &amp; payment settings. Plan is locked to what you pick.</p>
      <form onSubmit={create} className="space-y-3">
        <div><Label>Their name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="invite-name-input" /></div>
        <div><Label>Their email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="invite-email-input" /></div>
        <div><Label>Plan (locked for them)</Label>
          <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
            <SelectTrigger className="mt-1" data-testid="invite-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="credit_solo">{PLAN_LABELS.credit_solo}</SelectItem>
              <SelectItem value="bundle">{PLAN_LABELS.bundle}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" data-testid="invite-create-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white"><Mail className="h-4 w-4 mr-1.5" /> Create & email invite</Button>
      </form>
      {created && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="invite-created-box">
          <div className="text-xs font-semibold text-emerald-800">Ready to send to {created.name}:</div>
          <div className="text-xs font-mono break-all text-emerald-900 bg-white rounded border border-emerald-200 p-2" data-testid="invite-link-text">{created.link}</div>
          <div className="text-xs text-emerald-800" data-testid="invite-emailed-note">{created.emailed ? `Emailed to ${created.email} automatically.` : "Email not sent — copy the link below."}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => copy(created.link)} data-testid="invite-copy-btn"><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
            <a href={mailto(created)}><Button size="sm" variant="outline" data-testid="invite-email-btn"><Mail className="h-3.5 w-3.5 mr-1" /> Open in my mail app</Button></a>
          </div>
        </div>
      )}
      {invites.length > 0 && (
        <div className="pt-1 space-y-1.5" data-testid="invite-list">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">Sent invites</div>
          {invites.map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-2 text-xs rounded border border-slate-100 px-2.5 py-2" data-testid={`invite-row-${i.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-700 truncate">{i.name}</div>
                <div className="text-slate-400 truncate">{i.email}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${i.status === "joined" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : i.status === "expired" ? "bg-slate-100 text-slate-500 border border-slate-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid={`invite-status-${i.id}`}>
                  {i.status === "joined" ? "Joined" : i.status === "expired" ? "Expired" : "Pending"}
                </span>
                {i.status === "pending" && (
                  <>
                    <button onClick={() => resend(i)} className="text-slate-400 hover:text-[#0B2545]" title={i.emailed_at ? "Resend invite email" : "Send invite email"} data-testid={`invite-send-${i.id}`}><Mail className="h-3.5 w-3.5" /></button>
                    <button onClick={() => copy(linkFor(i.token))} className="text-slate-400 hover:text-[#0B2545]" title="Copy link" data-testid={`invite-copy-${i.id}`}><Copy className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Organizations() {
  const [orgs, setOrgs] = useState(null);
  const [form, setForm] = useState({ company_name: "", owner_name: "", owner_email: "", owner_password: "", plan: "credit_solo" });
  const [denied, setDenied] = useState(false);

  const load = () => api.get("/platform/orgs").then((r) => setOrgs(r.data)).catch((e) => {
    if (e?.response?.status === 403) setDenied(true);
    else toast.error(errMsg(e));
  });
  useEffect(() => { load(); }, []);

  const changePlan = async (oid, plan) => {
    try {
      await api.patch(`/platform/orgs/${oid}/plan`, { plan });
      toast.success("Plan updated");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/orgs", form);
      toast.success(`${form.company_name} workspace created`);
      setForm({ company_name: "", owner_name: "", owner_email: "", owner_password: "", plan: "credit_solo" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  if (denied) return <div className="py-20 text-center text-slate-400 text-sm">Platform owner access only.</div>;
  if (!orgs) return <div className="py-20 text-center text-slate-400 text-sm">Loading organizations…</div>;

  return (
    <div className="space-y-5" data-testid="organizations-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">White-Label Organizations</h1>
        <p className="text-sm text-slate-500">Every agency running on your platform. Each gets an isolated, self-branded workspace.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200">
              {["Organization", "Plan", "Owner", "Staff", "Clients", "Disputes", "Revenue", "Since"].map((h) => (
                <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} className="border-b border-slate-100" data-testid={`org-row-${o.id}`}>
                  <td className="py-2.5 px-3.5">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-slate-800">{o.name}</span>
                      {o.id === "org" && <span className="text-[10px] font-mono uppercase tracking-wide bg-slate-900 text-white rounded px-1.5 py-0.5">Yours</span>}
                      {o.source && <span className="text-[10px] font-mono uppercase tracking-wide bg-sky-100 text-sky-800 rounded px-1.5 py-0.5" data-testid={`org-source-${o.id}`}>via {o.source}</span>}
                    </div>
                  </td>
                  <td className="py-2.5 px-3.5">
                    {o.id === "org" ? <span className="text-xs font-semibold text-slate-500">Platform</span> : (
                      <Select value={o.plan || "credit_solo"} onValueChange={(v) => changePlan(o.id, v)}>
                        <SelectTrigger className="h-8 w-56 text-xs" data-testid={`org-plan-select-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="credit_solo">Credit only — $275 + $25/mo</SelectItem>
                          <SelectItem value="bundle">Tax + Credit bundle — $900 · $0/mo</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {o.id !== "org" && (
                      <div className={`text-[10px] font-mono uppercase tracking-wide mt-1 ${o.refundable ? "text-emerald-700" : "text-slate-400"}`} data-testid={`org-refundable-${o.id}`}>
                        {o.refundable ? "Refund-eligible" : "Non-refundable — first client submitted"}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3.5 text-slate-600">{o.owner_name || "—"}<div className="text-xs text-slate-400">{o.owner_email}</div></td>
                  <td className="py-2.5 px-3.5 text-slate-600">{o.staff}</td>
                  <td className="py-2.5 px-3.5 text-slate-600">{o.clients}</td>
                  <td className="py-2.5 px-3.5 text-slate-600">{o.disputes}</td>
                  <td className="py-2.5 px-3.5 text-slate-700 font-medium">{money(o.revenue)}</td>
                  <td className="py-2.5 px-3.5 text-slate-500">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-4">
        <InvitePanel />
        <form onSubmit={create} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 h-fit">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Plus className="h-4 w-4" /> Provision a new agency</div>
          <p className="text-xs text-slate-400">Sell the platform: create a licensed workspace and hand the owner their login.</p>
          <div><Label>Company name</Label><Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" data-testid="org-company-input" /></div>
          <div><Label>Owner name</Label><Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1" data-testid="org-owner-input" /></div>
          <div><Label>Owner email</Label><Input type="email" required value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="mt-1" data-testid="org-email-input" /></div>
          <div><Label>Owner password</Label><Input required minLength={8} value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="mt-1" data-testid="org-password-input" /></div>
          <div><Label>Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger className="mt-1" data-testid="org-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit_solo">Credit only — $275 + $25/mo</SelectItem>
                <SelectItem value="bundle">Tax + Credit bundle — $900 · $0/mo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="org-create-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Create workspace</Button>
        </form>
        </div>
      </div>
    </div>
  );
}
